import type { StaffCapability } from '@/api/schemas/me';
import { useAuthStore } from './auth-store';
import { useMyCapabilities, verdictOf, type CapabilityVerdict } from './capabilities';

/**
 * What opens a section of the panel:
 *
 *   - a capability — a STAFF member needs it; an ADMIN never does,
 *   - `'ADMIN'` — the ADMIN role and nobody else,
 *   - `'ANY'` — whoever may sign in to the panel at all,
 *   - `'STORE'` — a store owner (role USER with an active store, 2026-11-02),
 *     and nobody else: an ADMIN or STAFF member reads stores from `/stores`.
 */
export type SectionGate = StaffCapability | 'ADMIN' | 'ANY' | 'STORE';

/**
 * ko-admin#190 — THE map from section to the capability that opens it: its
 * list, its detail and its ordinary actions (spec «Panel claro» §A.4). The
 * sidebar and the router both read it, so the menu cannot offer a door the
 * router then closes, and the router cannot let through what the menu hid.
 *
 * Read off the backend's controllers after ko-backend#842, where the same
 * capability sits on each section's list route. Money and irreversible actions
 * inside a section keep their own capability, gated at the button with
 * `CapabilityAction`.
 *
 * Keys are section roots; a detail (`/orders/$id`) belongs to the longest root
 * it starts with. A path under no key is not a section and is let through.
 */
export const SECTION_CAPABILITY: Readonly<Record<string, SectionGate>> = {
  // ko-admin#273 — the Panel is everybody's (spec §A.4): the status arrives
  // cut to the reader's capabilities, and the figures inside it stay behind
  // VIEW_ANALYTICS, which is what their `admin/analytics` routes ask for.
  '/': 'ANY',
  '/users': 'MANAGE_USERS',
  '/users/kyc': 'MANAGE_USERS',
  '/users/strikes': 'MANAGE_USERS',
  '/listings': 'REVIEW_LISTINGS',
  // ko-backend#1090 — the store queue rides the listing reviewers' capability.
  '/stores': 'REVIEW_LISTINGS',
  // ko-backend#1057 — orders, trades and disputes left MANAGE_USERS.
  '/orders': 'MANAGE_DISPUTES',
  '/trades': 'MANAGE_DISPUTES',
  '/disputes': 'MANAGE_DISPUTES',
  // KO Hub — its own capability; money inside a case stays on MOVE_ESCROW.
  '/kohub': 'MANAGE_KOHUB',
  '/escrow': 'MOVE_ESCROW',
  '/pending-decisions': 'MOVE_ESCROW',
  '/moderation': 'MANAGE_USERS',
  '/tournaments': 'MANAGE_TOURNAMENTS',
  '/tournaments/prizes': 'MANAGE_TOURNAMENTS',
  '/anticheat': 'MANAGE_TOURNAMENTS',
  '/questions': 'REVIEW_QUESTION',
  '/question-banks': 'REVIEW_QUESTION',
  '/questions/proposals': 'REVIEW_QUESTION',
  '/creators': 'ADMIN',
  '/koins': 'ADJUST_KOINS',
  '/payouts': 'VIEW_PAYOUTS',
  '/tiers': 'ADJUST_KOINS',
  '/campaigns': 'ADMIN',
  '/referrals': 'MANAGE_USERS',
  '/reports': 'VIEW_ANALYTICS',
  '/staff': 'ADMIN',
  '/invitations': 'ADMIN',
  '/taxonomy': 'ADMIN',
  '/package-profiles': 'REVIEW_LISTINGS',
  '/config': 'ADMIN',
  // #195 — no capability describes "read the whole log", on purpose.
  '/audit': 'ADMIN',
  '/outbox': 'ADMIN',
  // A STAFF member who changes phone has to be able to re-enrol their MFA.
  '/security': 'ANY',
  // The store owner's panel: their own store, nothing of the platform.
  '/tienda': 'STORE',
  '/tienda/publicaciones': 'STORE',
  '/tienda/pedidos': 'STORE',
  '/tienda/kohub': 'STORE',
  '/tienda/cobros': 'STORE',
  '/tienda/perfil': 'STORE',
};

/** The sections a store owner may open: theirs, and the second factor. */
export function isStoreSection(pathname: string): boolean {
  return SECTION_CAPABILITY[sectionOf(pathname) ?? ''] === 'STORE';
}

/**
 * The section root `pathname` belongs to: the longest key it equals or sits
 * under. Longest, because `/users/strikes` must not be decided by `/users`;
 * by whole segments, because `/question-banks` is not under `/questions`.
 */
export function sectionOf(pathname: string): string | null {
  let best: string | null = null;
  for (const root of Object.keys(SECTION_CAPABILITY)) {
    const matches =
      root === '/' ? pathname === '/' : pathname === root || pathname.startsWith(`${root}/`);
    if (matches && (best === null || root.length > best.length)) best = root;
  }
  return best;
}

/**
 * Whether `role` may open the section behind `pathname`.
 *
 * An ADMIN opens everything (ko-backend#842: the capability guard lets that
 * role past). A STAFF member opens what their capabilities open, and a
 * capability the panel could not find out about counts as open: the backend is
 * the boundary, and a notice naming what to grant beats a menu with nothing in
 * it. Any other role — or none — opens nothing.
 */
export function canOpen(
  pathname: string,
  role: string | null,
  verdict: (capability: string) => CapabilityVerdict,
): boolean {
  const section = sectionOf(pathname);
  const gate = section === null ? null : SECTION_CAPABILITY[section];
  // A store owner opens their own sections and the second factor; the
  // platform is not theirs, whatever the URL says.
  if (role === 'USER') return gate === 'STORE' || section === '/security';
  if (gate === 'STORE') return false;
  if (role === 'ADMIN') return true;
  if (role !== 'STAFF') return false;
  if (gate === null || gate === 'ANY') return true;
  if (gate === 'ADMIN') return false;
  return verdict(gate) !== 'denied';
}

/**
 * `canOpen` for the signed-in operator, for screens that link across sections.
 *
 * A session whose `user` did not survive (an older persisted store) is treated
 * as able to open everything, for the reason the sidebar gives: a panel that
 * stops navigating is a worse failure than a link that answers 403.
 */
export function useCanOpen(): (pathname: string) => boolean {
  const role = useAuthStore((s) => s.user?.role ?? null);
  const { data } = useMyCapabilities();
  return (pathname) => role === null || canOpen(pathname, role, (c) => verdictOf(data, c));
}
